import React, { useState, useEffect } from 'react';
import '../styles/EcosystemAnalysis.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

// Metal ecosystem stocks organized by category
const METAL_ECOSYSTEM = {
  'Industrial & Materials': {
    'CAT': { name: 'Caterpillar Inc', market: 'US' },
    'ITW': { name: 'Illinois Tool Works', market: 'US' },
    'MMM': { name: '3M Company', market: 'US' },
    'HON': { name: 'Honeywell International', market: 'US' },
    'RTX': { name: 'Raytheon Technologies', market: 'US' }
  },
  'Metals & Refining': {
    'PPL': { name: 'PPL Corporation', market: 'US' },
    'CVX': { name: 'Chevron', market: 'US' },
    'COP': { name: 'ConocoPhillips', market: 'US' },
    'XOM': { name: 'ExxonMobil', market: 'US' },
    'OXY': { name: 'Occidental Petroleum', market: 'US' }
  },
  'Energy & Infrastructure': {
    'EOG': { name: 'EOG Resources', market: 'US' },
    'WEC': { name: 'WEC Energy Group', market: 'US' },
    'DUK': { name: 'Duke Energy', market: 'US' },
    'SO': { name: 'Southern Company', market: 'US' },
    'NEE': { name: 'NextEra Energy', market: 'US' }
  },
  'Data Center & Logistics': {
    'DLR': { name: 'Digital Realty', market: 'US' },
    'EQIX': { name: 'Equinix', market: 'US' },
    'CCI': { name: 'Crown Castle', market: 'US' },
    'AMT': { name: 'American Tower', market: 'US' },
    'PLD': { name: 'Prologis', market: 'US' }
  }
};

export default function MetalEcosystemAnalysis() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('change');
  const [refreshTime, setRefreshTime] = useState(new Date());

  useEffect(() => {
    fetchStockData();
    const interval = setInterval(() => {
      fetchStockData();
      setRefreshTime(new Date());
    }, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchStockData = async () => {
    try {
      setLoading(true);
      const symbols = [];
      Object.values(METAL_ECOSYSTEM).forEach(category => {
        Object.keys(category).forEach(symbol => symbols.push(symbol));
      });

      // Fetch price and news data for all symbols
      const allStocks = [];
      for (const symbol of symbols) {
        try {
          const priceRes = await fetch(`${API_BASE_URL}/api/stock/${symbol}/details`);
          const priceData = priceRes.ok ? await priceRes.json() : null;
          
          const newsRes = await fetch(`${API_BASE_URL}/api/stock/${symbol}/news?limit=3`);
          const newsData = newsRes.ok ? await newsRes.json() : { news: [] };

          // Find category for this symbol
          let category = 'Other';
          for (const [cat, catStocks] of Object.entries(METAL_ECOSYSTEM)) {
            if (catStocks[symbol]) {
              category = cat;
              break;
            }
          }

          allStocks.push({
            symbol,
            category,
            name: METAL_ECOSYSTEM[category]?.[symbol]?.name || symbol,
            market: METAL_ECOSYSTEM[category]?.[symbol]?.market || 'US',
            current_price: priceData?.price || 0,
            day_change_percent: priceData?.change_percent || 0,
            day_change: priceData?.change || 0,
            market_cap: priceData?.market_cap || 0,
            pe_ratio: priceData?.pe_ratio || 0,
            news: newsData.news || []
          });
        } catch (err) {
          console.error(`Error fetching data for ${symbol}:`, err);
        }
      }

      setStocks(allStocks);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching ecosystem data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayedStocks = () => {
    let filtered = stocks;

    if (selectedCategory !== 'all') {
      filtered = stocks.filter(s => s.category === selectedCategory);
    }

    return filtered.sort((a, b) => {
      if (sortBy === 'change') {
        return (b.day_change_percent || 0) - (a.day_change_percent || 0);
      } else if (sortBy === 'price') {
        return (b.current_price || 0) - (a.current_price || 0);
      } else if (sortBy === 'symbol') {
        return a.symbol.localeCompare(b.symbol);
      }
      return 0;
    });
  };

  const displayedStocks = getDisplayedStocks();
  const categories = Object.keys(METAL_ECOSYSTEM);

  const getCategoryStats = () => {
    if (selectedCategory === 'all') {
      return {
        count: displayedStocks.length,
        avgChange: displayedStocks.length > 0 
          ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
          : 0
      };
    }
    return {
      count: displayedStocks.length,
      avgChange: displayedStocks.length > 0 
        ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
        : 0
    };
  };

  const stats = getCategoryStats();

  return (
    <div className="ecosystem-container">
      <div className="ecosystem-header">
        <h1>⚙️ Industrial & Materials Ecosystem</h1>
        <p className="subtitle">Manufacturing, Metals, Energy & Infrastructure Market Analysis</p>
        <div className="header-stats">
          <div className="stat">
            <span className="label">Stocks Monitored</span>
            <span className="value">{displayedStocks.length}</span>
          </div>
          <div className="stat">
            <span className="label">Avg Daily Change</span>
            <span className={`value ${stats.avgChange >= 0 ? 'positive' : 'negative'}`}>
              {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange}%
            </span>
          </div>
          <div className="stat">
            <span className="label">Last Updated</span>
            <span className="value">{refreshTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      <div className="ecosystem-controls">
        <div className="control-group">
          <label>Category:</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>Sort By:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="change">Daily Change %</option>
            <option value="price">Price</option>
            <option value="symbol">Symbol</option>
          </select>
        </div>
        <button className="refresh-btn" onClick={fetchStockData}>
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {error && (
        <div className="error-box">
          <p>{error}</p>
          <button onClick={fetchStockData}>Retry</button>
        </div>
      )}

      {loading && displayedStocks.length === 0 ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading Metal Ecosystem data...</p>
        </div>
      ) : (
        <div className="stocks-grid">
          {displayedStocks.map(stock => (
            <div key={stock.symbol} className={`stock-card ${stock.day_change_percent >= 0 ? 'positive' : 'negative'}`}>
              <div className="stock-header">
                <div className="stock-info">
                  <h3>{stock.symbol}</h3>
                  <p className="stock-name">{stock.name}</p>
                  <span className="stock-category">{stock.category}</span>
                </div>
                <div className="stock-price">
                  <div className="price">${stock.current_price?.toFixed(2) || 'N/A'}</div>
                  <div className={`change ${stock.day_change_percent >= 0 ? 'positive' : 'negative'}`}>
                    {stock.day_change_percent >= 0 ? '▲' : '▼'} {stock.day_change_percent?.toFixed(2) || 0}%
                  </div>
                </div>
              </div>

              <div className="stock-details">
                <div className="detail">
                  <span className="label">Market Cap:</span>
                  <span className="value">
                    {stock.market_cap ? `$${(stock.market_cap / 1e9).toFixed(2)}B` : 'N/A'}
                  </span>
                </div>
                <div className="detail">
                  <span className="label">P/E Ratio:</span>
                  <span className="value">{stock.pe_ratio?.toFixed(2) || 'N/A'}</span>
                </div>
              </div>

              {stock.news && stock.news.length > 0 && (
                <div className="stock-news">
                  <h4>Latest News</h4>
                  {stock.news.map((article, idx) => (
                    <div key={idx} className="news-item">
                      <p className="news-title">{article.title?.substring(0, 60)}...</p>
                      <p className="news-source">{article.source}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="ecosystem-info">
        <h3>Metal Market Overview</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>Gold Sector</h4>
            <p>Safe-haven asset driven by inflation and geopolitical risks. Major players: GLD, IAU, ABX, NEM.</p>
          </div>
          <div className="info-item">
            <h4>Silver Sector</h4>
            <p>Industrial + investment demand. More volatile than gold. Leaders: SLV, AG, HL.</p>
          </div>
          <div className="info-item">
            <h4>Industrial Metals</h4>
            <p>Copper, zinc, lithium tied to manufacturing and energy transition. Key: FCX, TX, SCCO.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
